import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useParams } from "react-router";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { _sessionManager, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { MODULE_INSURANCE } from "components/Menu/config/constants";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import { claimPaymentAmount } from "./InsuranceService";
import { useProducerInsurancePractices } from "./useProducerInsurancePractices";

export const useInsuranceClaimForm = () => {
  const { globalState } = useContext(AppContext);
  const { insurance_claim_id } = useParams();

  const { enqueueSnackbar } = useSnackbar();
  const { component: formStatusComponent, actions: formStatusActions, state: formStatusState } = useFormStatus();

  const {
    stateOptions,
    countyOptions,
    commodityOptions,
    typeOptions,
    practiceOptions,
    findPractice,
    isLoading: isLoadingPractices,
  } = useProducerInsurancePractices();

  const [stateName, setStateName] = useState(null);
  const [countyName, setCountyName] = useState(null);
  const [commodityName, setCommodityName] = useState(null);
  const [typeName, setTypeName] = useState(null);
  const [practiceName, setPracticeName] = useState(null);
  const hydratedFromClaim = useRef(false);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const [state, dispatch] = useReducer(propUpdateReducer, {
    is_active: true,
    module: MODULE_INSURANCE,
    producer_token: globalState.producer_token,
  });

  useEffect(() => {
    const _insurance_claim_id = String(insurance_claim_id) === "0" ? uuidv4() : insurance_claim_id;
    dispatch({
      type: "updateMany",
      payload: {
        id: _insurance_claim_id,
        reinsurance_year: globalState?.crop_year,
      },
    });
    hydratedFromClaim.current = false;
  }, [insurance_claim_id, globalState?.crop_year]);

  const loadInsuranceClaim = useCallback(async () => {
    try {
      if (String(insurance_claim_id) === "0") return;

      const producerFilter = {
        producer_token: globalState.producer_token,
        id: insurance_claim_id,
        module: MODULE_INSURANCE,
        is_active: true,
      };

      const _insuranceClaims = await UsersApiHelper.users_search("InsurancePlanClaim", producerFilter, globalState);

      if (_insuranceClaims?.length) {
        const claim = _insuranceClaims[0];
        dispatch({
          type: "updateMany",
          payload: { ...claim, payment_amount: claimPaymentAmount(claim) },
        });
      }
    } catch (err) {
      handleError(err);
    }
  }, [globalState, insurance_claim_id]);

  useEffect(() => {
    if (globalState?.producer_token && insurance_claim_id && String(insurance_claim_id) !== "0") {
      loadInsuranceClaim();
    }
  }, [globalState?.producer_token, insurance_claim_id, loadInsuranceClaim]);

  useEffect(() => {
    if (hydratedFromClaim.current || String(insurance_claim_id) === "0") return;
    if (!state?.location_state_name && !state?.commodity_name) return;

    setStateName(state.location_state_name || null);
    setCountyName(state.location_county_name || null);
    setCommodityName(state.commodity_name || null);
    setTypeName(state.type_name || null);
    setPracticeName(state.practice_name || null);
    hydratedFromClaim.current = true;
  }, [insurance_claim_id, state]);

  const selectedPractice = useMemo(() => {
    if (!stateName || !countyName || !commodityName || !typeName || !practiceName) return null;
    return findPractice(stateName, countyName, commodityName, typeName, practiceName);
  }, [stateName, countyName, commodityName, typeName, practiceName, findPractice]);

  const insurancePlans = selectedPractice?.insurancePlans ?? [];

  const stampLocationFields = useCallback((next) => {
    dispatch({
      type: "updateMany",
      payload: {
        location_state_name: next.stateName ?? null,
        location_county_name: next.countyName ?? null,
        commodity_name: next.commodityName ?? null,
        type_name: next.typeName ?? null,
        practice_name: next.practiceName ?? null,
        state_name: next.stateName ?? null,
        county_name: next.countyName ?? null,
      },
    });
  }, []);

  const setField = useCallback(
    (key, value) => {
      formStatusActions.setIsFormDirty(true, false);

      const next = {
        stateName,
        countyName,
        commodityName,
        typeName,
        practiceName,
      };

      if (key === "state") {
        next.stateName = value;
        next.countyName = null;
        next.commodityName = null;
        next.typeName = null;
        next.practiceName = null;
        setStateName(value);
        setCountyName(null);
        setCommodityName(null);
        setTypeName(null);
        setPracticeName(null);
      } else if (key === "county") {
        next.countyName = value;
        next.commodityName = null;
        next.typeName = null;
        next.practiceName = null;
        setCountyName(value);
        setCommodityName(null);
        setTypeName(null);
        setPracticeName(null);
      } else if (key === "commodity") {
        next.commodityName = value;
        next.typeName = null;
        next.practiceName = null;
        setCommodityName(value);
        setTypeName(null);
        setPracticeName(null);
      } else if (key === "type") {
        next.typeName = value;
        next.practiceName = null;
        setTypeName(value);
        setPracticeName(null);
      } else if (key === "practice") {
        next.practiceName = value;
        setPracticeName(value);
      }

      stampLocationFields(next);

      if (key === "practice") {
        dispatch({
          type: "updateMany",
          payload: {
            insurance_plan_id: null,
            insurance_plan_name: "",
          },
        });
      }
    },
    [stateName, countyName, commodityName, typeName, practiceName, formStatusActions, stampLocationFields]
  );

  const locationPickers = useMemo(
    () => ({
      stateName,
      countyName,
      commodityName,
      typeName,
      practiceName,
      stateOptions,
      countyOptions: countyOptions(stateName),
      commodityOptions: commodityOptions(stateName, countyName),
      typeOptions: typeOptions(stateName, countyName, commodityName),
      practiceOptions: practiceOptions(stateName, countyName, commodityName, typeName).map((p) => p.practice_name),
      setField,
      isLoading: isLoadingPractices,
    }),
    [
      stateName,
      countyName,
      commodityName,
      typeName,
      practiceName,
      stateOptions,
      countyOptions,
      commodityOptions,
      typeOptions,
      practiceOptions,
      setField,
      isLoadingPractices,
    ]
  );

  const handleChangeStateSelect = (event) => {
    formStatusActions.setIsFormDirty(true, false);

    if (event.target.name === "insurance_plan") {
      const plan = event.target.value;
      dispatch({
        type: "updateMany",
        payload: {
          insurance_plan_id: plan?.id ?? null,
          insurance_plan_name: plan?.insurance_plan_name ?? "",
        },
      });
    } else {
      dispatch({
        type: "update",
        payload: { key: event.target.name, value: event.target.value },
      });
    }
  };

  const handleChangeState = (event, value) => {
    if (!event?.target) return;

    const eventValue = event.target.value;
    const eventName = event.target.name;

    value = value ?? eventValue;

    if (state[eventName] !== value) {
      formStatusActions.setIsFormDirty(true, false);

      dispatch({
        type: "update",
        payload: { key: eventName, value: value },
      });
    }
  };

  const isValid = useCallback(() => {
    return Boolean(state?.id && String(state.id) !== "0");
  }, [state?.id]);

  const buildInsurancePlanClaimPayload = useCallback(() => {
    const policyTrimmed = (state?.policy_number != null ? String(state.policy_number) : "").trim().slice(0, 7);
    const policy_number = policyTrimmed === "" ? null : policyTrimmed;
    const insurance_plan_id = state?.insurance_plan_id ?? null;
    const insurance_plan_name = insurance_plan_id ? (state?.insurance_plan_name ?? "").trim() : "";

    return {
      ...state,
      producer_token: state?.producer_token ?? globalState?.producer_token,
      module: MODULE_INSURANCE,
      policy_number,
      insurance_plan_id,
      insurance_plan_name,
      location_state_name: stateName ?? state?.location_state_name ?? null,
      location_county_name: countyName ?? state?.location_county_name ?? null,
      commodity_name: commodityName ?? state?.commodity_name ?? null,
      type_name: typeName ?? state?.type_name ?? null,
      practice_name: practiceName ?? state?.practice_name ?? null,
      state_name: stateName ?? state?.state_name ?? null,
      county_name: countyName ?? state?.county_name ?? null,
    };
  }, [state, globalState?.producer_token, stateName, countyName, commodityName, typeName, practiceName]);

  const updateInsuranceClaim = useCallback(async () => {
    try {
      if (globalState.producer_token && formStatusState?.isFormDirty) {
        formStatusActions.setIsSaving(true);

        const api = new ApiHelper({
          baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
          config: {},
          sessionManager: _sessionManager,
          _instance: null,
        });

        await api.callPost(
          "InsurancePlanClaim",
          {
            module: MODULE_INSURANCE,
            InsurancePlanClaim: [buildInsurancePlanClaimPayload()],
            producer_token: globalState?.producer_token,
          },
          globalState
        );

        loadInsuranceClaim();
        formStatusActions.setIsSaving(false);
      }
    } catch (err) {
      formStatusActions.setIsFormDirty(true, false);
      formStatusActions.setIsSaving(false);
      handleError(err);
    }
  }, [buildInsurancePlanClaimPayload, globalState.producer_token, formStatusState?.isFormDirty, loadInsuranceClaim]);

  useEffect(() => {
    if (isValid() && globalState.producer_token && formStatusState?.isFormDirty) {
      updateInsuranceClaim();
    }
  }, [isValid, globalState.producer_token, formStatusState?.isFormDirty, updateInsuranceClaim]);

  return {
    state,
    insurancePlans,
    formStatusComponent,
    formStatusActions,
    formStatusState,
    isValid,
    locationPickers,
    handleChangeStateSelect,
    handleChangeState,
    updateInsuranceClaim,
    loadInsuranceClaim,
  };
};
